import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { flexRender, Header } from '@tanstack/react-table';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TableSorter } from './TableSorter';

interface Props<T> {
  columnHeader: Header<T, unknown>;
}

const CONTENT_STYLES = 'flex w-full justify-between gap-3 items-center px-4 pt-[10px] pb-1 rounded-regular';

export const HeaderCell = <T,>({ columnHeader }: Props<T>) => {
  const { column, id, isPlaceholder } = columnHeader;
  const { meta, header } = column.columnDef;
  const sortOrder = column.getIsSorted();
  const canSort = column.getCanSort();
  const isSorted = column.getIsSorted();

  const { t } = useTranslation(['accessibilityDescriptions']);

  const ariaSort = React.useMemo(() => {
    if (!canSort) {
      return undefined;
    }

    if (isSorted === false) {
      return 'none';
    }

    return isSorted === 'asc' ? 'ascending' : 'descending';
  }, [canSort, isSorted]);

  const value = flexRender(header, columnHeader.getContext());

  const content = (
    <span
      className={cn('flex-grow whitespace-nowrap text-neutral-40 body-2', meta?.headerCellClassName, {
        'text-center': meta?.align === 'center',
        'text-left': !isDefined(meta?.align) || meta.align === 'left',
        'text-right': meta?.align === 'right',
      })}
    >
      {isPlaceholder ? null : value}
    </span>
  );

  return (
    <th aria-sort={ariaSort}>
      {canSort ?
        <>
          <div id={`sorter-${id}`} className="hidden">
            {isSorted === false && t('accessibilityDescriptions:sort_asc', { headerName: value?.toString() })}
            {isSorted === 'asc' && t('accessibilityDescriptions:sort_desc', { headerName: value?.toString() })}
            {isSorted === 'desc' && t('accessibilityDescriptions:unsort', { headerName: value?.toString() })}
          </div>
          <button
            type="button"
            aria-describedby={`sorter-${id}`}
            className={`${CONTENT_STYLES} cursor-pointer !-outline-offset-1`}
            onClick={column.getToggleSortingHandler()}
          >
            {content}
            <TableSorter sortOrder={sortOrder !== false ? sortOrder : null} />
          </button>
        </>
      : <div className={CONTENT_STYLES}>{content}</div>}
    </th>
  );
};
